import React, { useEffect, useRef, useState } from 'react';
import podiumService from '../../services/podiumService';

const GOLD = 'rgb(var(--brand-gold-rgb))';
const KIND_LABEL = { earnings_call: 'Earnings call', analyst_call: 'Post-earnings analyst call', conference: 'Conference', investor_day: 'Investor day', other: 'Event' };
const mmss = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const isVoice = who => /^VOICE_|^SPEAKER_/.test(who || '');

function RoleTag({ role, rule }) {
  const t = role === 'operator' ? 'operator' : role === 'analyst' ? 'analyst' : role === 'management' ? 'management · unnamed' : role || '';
  return <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-gray-400">{t}{rule && rule !== 'voice' ? ` · ${rule}` : ''}</span>;
}

// A speaker name in the transcript: a LINK to the person's profile (executives, hosts and analysts alike) with a hover
// card (firm, role, companies covered, calls, fingerprint); an unnamed voice links to its open review item instead.
function Speaker({ run, cards, voiceReviews, onPerson, onReview }) {
  const [hover, setHover] = useState(false);
  if (isVoice(run.who)) {
    const rid = voiceReviews[run.voice];
    return <button onClick={() => rid && onReview(rid)} className="font-medium underline decoration-dotted" style={{ color: 'rgb(245 158 11)' }} title={rid ? 'open the review item' : 'no open review'}>{run.who}</button>;
  }
  if (!run.person_key) return <span className="font-medium" style={{ opacity: 0.6 }}>{run.who}</span>;
  const c = cards[run.person_key] || {};
  return (
    <span className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button onClick={() => onPerson(run.person_key)} className="font-medium underline decoration-dotted" style={{ color: GOLD }}>{run.who}</button>
      {hover && (
        <span className="absolute left-0 top-5 z-10 w-64 rounded border border-gray-200 bg-white p-2 text-xs shadow-lg dark:border-gray-700 dark:bg-[#161b22]">
          <span className="block font-medium text-gray-900 dark:text-gray-100">{c.name || run.who}</span>
          <span className="block text-gray-500 dark:text-gray-400">{c.role_hint || ''}{c.firm ? ` · ${c.firm}` : ''}</span>
          <span className="block text-gray-500 dark:text-gray-400">covers {(c.companies || []).join(', ') || '—'} · {c.calls || 0} call{c.calls === 1 ? '' : 's'} · {c.fingerprints ? 'voice on file' : 'no voice yet'}</span>
          {c.spelling_unverified && <span className="block text-amber-500">spelling unverified</span>}
        </span>)}
    </span>
  );
}

// The Call page: the transcript as speaker runs (voice-attributed, cue-named, every name a link), the deterministic
// structure beside it (click a question → the transcript scrolls there), the naming evidence per voice, and this
// call's review items. `jumpTo` (seconds) scrolls on open — used by the profile's question links.
export default function PodiumCallView({ slug, jumpTo, onBack, onPerson, onReview }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [side, setSide] = useState('structure');
  const [target, setTarget] = useState(jumpTo != null ? Number(jumpTo) : null);
  const runRefs = useRef({});
  useEffect(() => { setD(null); podiumService.getCall(slug).then(r => setD(r.data)).catch(() => setErr('could not load the call')); }, [slug]);
  useEffect(() => {
    if (!d || target == null) return;
    const idx = d.runs.findIndex((r, i) => r.start <= target && (i === d.runs.length - 1 || d.runs[i + 1].start > target));
    const el = runRefs.current[idx]; if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.style.outline = `1px solid ${GOLD}`; setTimeout(() => { el.style.outline = ''; }, 2500); }
  }, [d, target]);
  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!d) return <p className="text-sm text-gray-500">Loading…</p>;
  const c = d.call; const st = d.structure || {}; const qa = st.qa || []; const cards = d.cards || {}; const voiceReviews = d.voice_reviews || {};
  const openReviews = (d.review || []).filter(r => r.status === 'open');
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs uppercase tracking-[0.15em] text-gray-400 hover:text-gray-200">← Podium</button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">{c.ticker} · {c.fiscal_label || ''} · {KIND_LABEL[c.kind] || c.kind}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{c.title}{c.held_at ? ` · ${c.held_at.slice(0, 10)}` : ''} · {Math.round((c.duration_s || 0) / 60)} min · {qa.length} questions
            {openReviews.length ? <span className="ml-2 text-amber-500">· {openReviews.length} open review{openReviews.length > 1 ? 's' : ''}</span> : <span className="ml-2 text-green-500">· reviewed</span>}</p>
        </div>
        {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-xs underline text-gray-400">webcast</a>}
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 space-y-3 max-h-[75vh] overflow-y-auto scrollbar-thin">
          {d.runs.map((r, i) => (
            <div key={i} ref={el => { runRefs.current[i] = el; }} className="text-sm rounded px-1">
              <div className="flex items-baseline gap-2">
                <button onClick={() => setTarget(r.start)} className="tabular-nums text-[11px] text-gray-400">{mmss(r.start)}</button>
                <Speaker run={r} cards={cards} voiceReviews={voiceReviews} onPerson={onPerson} onReview={onReview} />
                <RoleTag role={r.role} rule={r.rule} />
              </div>
              <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <nav className="flex gap-2">
            {['structure', 'evidence', 'review'].map(k => (
              <button key={k} onClick={() => setSide(k)} className="px-2 py-1 text-[11px] uppercase tracking-[0.15em] rounded border"
                style={side === k ? { color: GOLD, borderColor: GOLD } : { borderColor: 'transparent', opacity: 0.7 }}>{k}</button>))}
          </nav>
          {side === 'structure' && (
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-3">
              {st.remarks && <p className="text-gray-500 dark:text-gray-400">{st.remarks.label || 'Prepared remarks'}: {(st.remarks.speakers || []).map(s => s.person).join(', ') || '—'}</p>}
              {qa.map(q => {
                const at = (q.question || [{}])[0]?.start;
                return (
                  <div key={q.n} className="border-t border-gray-100 dark:border-gray-800 pt-2">
                    <button onClick={() => at != null && setTarget(at)} className="font-medium text-left hover:underline">Q{q.n} · {q.analyst || '?'}{q.firm ? ` (${q.firm})` : ''}{at != null ? ` · ${mmss(at)}` : ''}</button>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{(q.question || []).length} question{(q.question || []).length === 1 ? '' : 's'} · answered by {(q.answers || []).map(a => a.person).filter((v, i, a) => a.indexOf(v) === i).join(', ') || '—'}{q.closing ? ` · “${q.closing}”` : ''}</div>
                  </div>);
              })}
            </div>)}
          {side === 'evidence' && (
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-2">
              {Object.entries(d.voice_names || {}).map(([v, info]) => (
                <div key={v} className="border-t border-gray-100 dark:border-gray-800 pt-2 first:border-0 first:pt-0">
                  <div className="font-medium">{v} → {info.person || <span className="text-amber-500">unnamed</span>}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{Object.entries(info.claims || {}).map(([k, n]) => `${k}: ${n}`).join(' · ')}</div>
                  {(info.evidence || []).slice(0, 3).map((e, i) => <div key={i} className="text-xs text-gray-400">{e.at != null ? `${mmss(e.at)} ` : ''}{e.cue}</div>)}
                  {info.candidates && <div className="text-xs text-amber-500">candidates: {info.candidates.join(', ')}</div>}
                </div>))}
            </div>)}
          {side === 'review' && (
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-2">
              {(d.review || []).length === 0 && <p className="text-gray-500">Nothing to review on this call.</p>}
              {(d.review || []).map(r => (
                <div key={r.id} className="border-t border-gray-100 dark:border-gray-800 pt-2 first:border-0 first:pt-0">
                  <span className={'text-[10px] uppercase tracking-[0.15em] ' + (r.status === 'open' ? 'text-amber-500' : 'text-green-500')}>{r.kind} · {r.status}</span>
                  <div>{r.payload.voice || ''} {r.payload.heard || ''}{r.payload.candidates?.length ? ` · candidates: ${r.payload.candidates.join(', ')}` : ''}</div>
                  {r.resolution?.person && <div className="text-xs text-gray-500">→ {r.resolution.person}</div>}
                </div>))}
              <button onClick={() => onReview(null)} className="text-xs underline text-gray-400">Decide in the Review tab</button>
            </div>)}
        </div>
      </div>
    </div>
  );
}
